import { useEffect, useState, type ReactNode } from 'react'
import {
  isEmpty,
  isPermissionEntityScope,
  type PermissionDefinition,
  type PermissionEntityId,
  type PermissionType,
} from '@terros/common'
import { Code, Group, Radio, Skeleton, Stack, Text } from '@mantine/core'
import { useToastError } from '@hooks'
import { usePermissionCache, usePermissions, useProfile, useUpdatePermission } from '@front/model'
import { getScopeEditorProps, stringifyPermissionScope } from '../util'
import { type PermissionEditScope } from '../types'
import { TButton } from '../../buttons'
import { type PermGraphNode } from './types'
import { getPermGraphNodeAccess } from './permGraphNodeAccess'

type Props = {
  definition: PermissionDefinition
  node: PermGraphNode
  permissionType: PermissionType
  readOnly: boolean
  viewedEntityId: PermissionEntityId
  onClose: () => void
}

export const PermGraphNodeEditor = ({
  definition,
  node,
  permissionType,
  readOnly,
  viewedEntityId,
  onClose,
}: Props): ReactNode => {
  const { user } = useProfile()
  const access = getPermGraphNodeAccess({ currentUserId: user?.userId, node })

  if (readOnly && node.isTarget) return <EditorTitle>Restore to edit these permissions.</EditorTitle>
  if (!access.canEdit) return <EditorTitle>{access.reason}</EditorTitle>

  return (
    <Stack gap='xs'>
      <EditorTitle>
        Set <NameChip>{definition.label}</NameChip> for <NameChip>{node.label}</NameChip>
        {!node.isTarget && ' - changing this affects everyone who inherits from it.'}
      </EditorTitle>
      <NodeScopeForm node={node} permissionType={permissionType} viewedEntityId={viewedEntityId} onClose={onClose} />
    </Stack>
  )
}

const NameChip = ({ children }: { children: ReactNode }): ReactNode => (
  <Code bg='light-dark(var(--mantine-color-gray-2), var(--mantine-color-dark-5))' c='var(--mantine-color-text)'>
    {children}
  </Code>
)

const EditorTitle = ({ children }: { children: ReactNode }): ReactNode => (
  <Text c='dimmed' size='sm'>
    {children}
  </Text>
)

const NodeScopeForm = ({
  node,
  permissionType,
  viewedEntityId,
  onClose,
}: Omit<Props, 'definition' | 'readOnly'>): ReactNode => {
  const { data, isLoading, error } = usePermissions(node.entityId)
  const { availableScopes, currentScope } = getScopeEditorProps(data?.list[permissionType], node.entityId)
  const [pendingScope, setPendingScope] = useState<PermissionEditScope>(currentScope)
  const { update, isUpdating, error: updateError } = useUpdatePermission()
  const cache = usePermissionCache()
  useToastError(updateError)

  useEffect(() => {
    setPendingScope(currentScope)
  }, [currentScope, node.entityId])

  const applyScope = (): void => {
    update({ entityId: node.entityId, permissionType, scope: pendingScope })
      .then(() => cache.clearEntityPermission(viewedEntityId))
      .catch(() => setPendingScope(currentScope))
  }

  if (isLoading) return <Skeleton height={96} />
  if (error) return <EditorTitle>{error}</EditorTitle>
  if (isEmpty(availableScopes)) return <EditorTitle>This source is read-only for you.</EditorTitle>

  return (
    <Stack gap='xs'>
      <Radio.Group
        value={pendingScope}
        onChange={(value) => {
          if (value === 'default' || isPermissionEntityScope(value)) setPendingScope(value)
        }}
      >
        <Stack gap='xs'>
          {availableScopes.map((scope) => (
            <Radio key={scope} label={stringifyPermissionScope(scope)} value={scope} />
          ))}
          <Radio label='Use inherited default' value='default' />
        </Stack>
      </Radio.Group>
      <Group gap='xs'>
        <TButton
          disabled={pendingScope === currentScope}
          loading={isUpdating}
          size='xs'
          variant='primary'
          onClick={applyScope}
        >
          Apply
        </TButton>
        <TButton size='xs' variant='gray' onClick={onClose}>
          Cancel
        </TButton>
      </Group>
    </Stack>
  )
}
